import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ScanEvent, ScannerAdapterStatus, ScannerSource } from './barcode-scanner.models';

interface ScannerBridgeWindow extends Window {
  UlaapScanner?: {
    emitScan?: (barcode: string, symbology?: string, source?: ScannerSource) => void;
  };
  ulaapScannerScan?: (barcode: string, symbology?: string, source?: ScannerSource) => void;
}

interface HidNavigator extends Navigator {
  hid?: {
    requestDevice: (options: unknown) => Promise<unknown[]>;
  };
}

interface UsbNavigator extends Navigator {
  usb?: {
    requestDevice: (options: unknown) => Promise<unknown>;
  };
}

interface SerialNavigator extends Navigator {
  serial?: {
    requestPort: () => Promise<unknown>;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ScannerInputService {
  private readonly scanSubject = new Subject<ScanEvent>();
  readonly scan$: Observable<ScanEvent> = this.scanSubject.asObservable();

  private readonly statusSubject = new BehaviorSubject<ScannerAdapterStatus[]>(this.createInitialStatuses());
  readonly statuses$ = this.statusSubject.asObservable();

  private buffer = '';
  private lastKeyAt = 0;
  private lastBarcode = '';
  private lastBarcodeAt = 0;

  private readonly minLength = 3;
  private readonly interCharacterTimeoutMs = 140;
  private readonly duplicateWindowMs = 350;
  private readonly terminatorKeys = new Set(['Enter', 'Tab']);

  constructor(private zone: NgZone) {
    this.installKeyboardAdapter();
    this.installNativeBridgeAdapter();
    this.refreshExperimentalAvailability();
  }

  emitManualScan(value: string): void {
    this.emitScan(value, 'manual');
  }

  async probeExperimentalAdapter(source: Extract<ScannerSource, 'webhid' | 'webusb' | 'webserial'>): Promise<void> {
    if (source === 'webhid') {
      const hid = (navigator as HidNavigator).hid;
      this.updateStatus('webhid', {
        available: !!hid,
        active: false,
        detail: hid ? 'WebHID is available. Use a vendor/product filter before enabling on a production scanner.' : 'WebHID is not exposed by this browser.',
      });
      return;
    }

    if (source === 'webusb') {
      const usb = (navigator as UsbNavigator).usb;
      this.updateStatus('webusb', {
        available: !!usb,
        active: false,
        detail: usb ? 'WebUSB is available for USB-mode scanners with approved vendor filters.' : 'WebUSB is not exposed by this browser.',
      });
      return;
    }

    const serial = (navigator as SerialNavigator).serial;
    this.updateStatus('webserial', {
      available: !!serial,
      active: false,
      detail: serial ? 'WebSerial is available for scanners configured as serial devices.' : 'WebSerial is not exposed by this browser.',
    });
  }

  private installKeyboardAdapter(): void {
    window.addEventListener('keydown', (event) => this.handleKeyboardEvent(event), true);
  }

  private installNativeBridgeAdapter(): void {
    const bridgeWindow = window as ScannerBridgeWindow;
    const emitFromBridge = (barcode: string, symbology?: string, source?: ScannerSource): void => {
      this.emitScan(barcode, source || 'native-bridge', symbology);
    };

    bridgeWindow.UlaapScanner = {
      ...(bridgeWindow.UlaapScanner || {}),
      emitScan: emitFromBridge,
    };
    bridgeWindow.ulaapScannerScan = emitFromBridge;

    window.addEventListener('ulaap-scanner-scan', (event) => {
      const detail = event instanceof CustomEvent && event.detail && typeof event.detail === 'object'
        ? event.detail as Record<string, unknown>
        : {};
      const barcode = detail['barcode'] || detail['value'] || detail['data'];
      const symbology = detail['symbology'];
      const source = detail['source'];

      if (typeof barcode === 'string') {
        this.emitScan(
          barcode,
          this.normalizeBridgeSource(source),
          typeof symbology === 'string' ? symbology : undefined,
          detail
        );
      }
    });

    window.addEventListener('message', (event) => {
      const data = event.data;

      if (!data || typeof data !== 'object') {
        return;
      }

      const record = data as Record<string, unknown>;

      if (record['type'] !== 'ulaap-scanner-scan') {
        return;
      }

      const barcode = record['barcode'] || record['value'] || record['data'];
      const symbology = record['symbology'];
      const source = record['source'];

      if (typeof barcode === 'string') {
        this.emitScan(
          barcode,
          this.normalizeBridgeSource(source),
          typeof symbology === 'string' ? symbology : undefined,
          record
        );
      }
    });

    this.updateStatus('native-bridge', {
      available: true,
      active: true,
      detail: 'Native bridge callbacks are registered for WebView, Enterprise Browser, and intent wrappers.',
    });
  }

  private handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.defaultPrevented || !this.shouldCaptureKeyboardEvent(event)) {
      return;
    }

    const now = Date.now();

    if (now - this.lastKeyAt > this.interCharacterTimeoutMs) {
      this.buffer = '';
    }

    this.lastKeyAt = now;

    if (this.terminatorKeys.has(event.key)) {
      const candidate = this.buffer.trim();
      this.buffer = '';

      if (candidate.length >= this.minLength) {
        event.preventDefault();
        this.emitScan(candidate, 'keyboard');
      }

      return;
    }

    if (event.key === 'Escape') {
      this.buffer = '';
      return;
    }

    if (event.key.length === 1) {
      this.buffer += event.key;
    }
  }

  private shouldCaptureKeyboardEvent(event: KeyboardEvent): boolean {
    const target = event.target instanceof HTMLElement ? event.target : null;

    if (!target) {
      return true;
    }

    if (target.getAttribute('data-scanner-capture') === 'true') {
      return true;
    }

    const tagName = target.tagName.toLowerCase();
    return tagName !== 'input' && tagName !== 'textarea' && tagName !== 'select' && !target.isContentEditable;
  }

  private emitScan(value: string, source: ScannerSource, symbology?: string, raw?: unknown): void {
    const barcode = value.trim();

    if (!barcode) {
      return;
    }

    const now = Date.now();

    if (barcode === this.lastBarcode && now - this.lastBarcodeAt < this.duplicateWindowMs) {
      return;
    }

    this.lastBarcode = barcode;
    this.lastBarcodeAt = now;

    this.zone.run(() => {
      this.scanSubject.next({
        value: barcode,
        symbology,
        source,
        raw,
        timestamp: now,
      });
    });
  }

  private normalizeBridgeSource(source: unknown): ScannerSource {
    if (
      source === 'zebra-datawedge' ||
      source === 'honeywell-bridge' ||
      source === 'datalogic-bridge' ||
      source === 'webhid' ||
      source === 'webusb' ||
      source === 'webserial'
    ) {
      return source;
    }

    return 'native-bridge';
  }

  private refreshExperimentalAvailability(): void {
    this.updateStatus('webhid', {
      available: !!(navigator as HidNavigator).hid,
      active: false,
      detail: 'Optional. Prefer vendor bridge or keyboard wedge unless the scanner is configured for a supported HID mode.',
    });
    this.updateStatus('webusb', {
      available: !!(navigator as UsbNavigator).usb,
      active: false,
      detail: 'Optional. Requires scanner USB mode and production vendor/product filters.',
    });
    this.updateStatus('webserial', {
      available: !!(navigator as SerialNavigator).serial,
      active: false,
      detail: 'Optional. Useful for scanners configured as serial devices.',
    });
  }

  private createInitialStatuses(): ScannerAdapterStatus[] {
    return [
      {
        source: 'keyboard',
        label: 'Keyboard wedge',
        available: true,
        active: true,
        detail: 'Bluetooth HID and wedge scanners are captured with prefix/suffix and terminator handling.',
      },
      {
        source: 'manual',
        label: 'Manual entry',
        available: true,
        active: true,
        detail: 'Manual barcode entry is routed through the same scan pipeline.',
      },
      {
        source: 'native-bridge',
        label: 'Vendor bridge',
        available: false,
        active: false,
        detail: 'Waiting for a WebView, Enterprise Browser, DataWedge, Honeywell, or Datalogic bridge.',
      },
      {
        source: 'webhid',
        label: 'WebHID',
        available: false,
        active: false,
        detail: 'Optional experimental adapter.',
      },
      {
        source: 'webusb',
        label: 'WebUSB',
        available: false,
        active: false,
        detail: 'Optional experimental adapter.',
      },
      {
        source: 'webserial',
        label: 'WebSerial',
        available: false,
        active: false,
        detail: 'Optional serial adapter.',
      },
    ];
  }

  private updateStatus(source: ScannerSource, patch: Partial<ScannerAdapterStatus>): void {
    const nextStatuses = this.statusSubject.value.map((status) =>
      status.source === source ? { ...status, ...patch } : status
    );
    this.statusSubject.next(nextStatuses);
  }
}
